import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { SCENES, sceneAudio, sceneImg, sceneImgMobile } from "./onboardingConfig";

const API = process.env.REACT_APP_BACKEND_URL;
const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const TOTAL = SCENES.length;
const PAUSE = 350;

const ss = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch (_) { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (_) {} },
};

export default function OnboardingScenes() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const replay = new URLSearchParams(location.search).get("replay") === "1";
  const { user, loading, refresh } = useAuth();
  const { scene } = useParams();
  const n = Math.min(Math.max(parseInt(scene, 10) || 1, 1), TOTAL);
  const isFinal = n === TOTAL;
  const sc = SCENES[n - 1];

  const reduced = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ).current;
  const FADE = reduced ? 160 : 700;

  const [stage, setStage] = useState("out");
  const [locked, setLocked] = useState(true);
  const [muted, setMuted] = useState(() => ss.get("onb_muted") === "1");
  const [unlocked, setUnlocked] = useState(() => ss.get("onb_audio_unlocked") === "1");
  const [subIdx, setSubIdx] = useState(-1);

  const lockedRef = useRef(true);
  const mutedRef = useRef(muted);
  const unlockedRef = useRef(unlocked);
  const nRef = useRef(n);
  nRef.current = n;
  const audioRef = useRef(null);
  const tokenRef = useRef(0);
  const fallbackTimers = useRef([]);
  const pointerHandler = useRef(null);
  const touchStart = useRef(null);
  const musicRef = useRef(null);
  const musicRampRef = useRef(null);
  const narrNodesRef = useRef(null);
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const musicGainRef = useRef(null);
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(ss.get("onb_volume"));
    return Number.isFinite(v) ? Math.min(Math.max(v, 0), 1) : 1;
  });
  const volumeRef = useRef(volume);

  // Music sits far under the narration: ~2% idle, ducked to ~0.8% while
  // Hipparchia speaks. Narration plays at 100% of the master volume.
  const MUSIC_IDLE = 0.07;
  const MUSIC_DUCK = 0.035;

  // Web Audio graph — iOS Safari ignores HTMLMediaElement.volume, so all
  // levels (master slider, music bed, ducking) run through GainNodes.
  // Falls back to element.volume on browsers without AudioContext.
  const ensureCtx = useCallback(() => {
    if (ctxRef.current) {
      if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
      return ctxRef.current;
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const master = ctx.createGain();
      master.gain.value = volumeRef.current;
      master.connect(ctx.destination);
      const musicG = ctx.createGain();
      musicG.gain.value = 0;
      musicG.connect(master);
      ctxRef.current = ctx;
      masterGainRef.current = master;
      musicGainRef.current = musicG;
      return ctx;
    } catch (_) {
      return null;
    }
  }, []);

  const rampMusic = useCallback((target, ms = 400) => {
    const m = musicRef.current;
    if (!m) return;
    const g = musicGainRef.current;
    if (g && ctxRef.current) {
      const t = ctxRef.current.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(g.gain.value, t);
      g.gain.linearRampToValueAtTime(target, t + ms / 1000);
      return;
    }
    if (musicRampRef.current) clearInterval(musicRampRef.current);
    const goal = target * volumeRef.current;
    const steps = Math.max(4, Math.round(ms / 50));
    const v0 = m.volume;
    let i = 0;
    musicRampRef.current = setInterval(() => {
      i++;
      if (musicRef.current !== m) { clearInterval(musicRampRef.current); return; }
      m.volume = Math.max(0, Math.min(1, v0 + (goal - v0) * (i / steps)));
      if (i >= steps) clearInterval(musicRampRef.current);
    }, 50);
  }, []);

  // Soft ambience bed under the narration — plays once the audio context is
  // unlocked, loops across all scenes, follows the mute toggle. Fails silently
  // when /onboarding/audio/ambience.mp3 has not been uploaded yet.
  const startMusic = useCallback(() => {
    if (musicRef.current) return;
    const m = new Audio("/onboarding/audio/ambience.mp3");
    m.loop = true;
    m.preload = "auto";
    m.muted = mutedRef.current;
    m.onerror = () => { if (musicRef.current === m) musicRef.current = null; };
    musicRef.current = m;
    let wired = false;
    const ctx = ensureCtx();
    if (ctx && musicGainRef.current) {
      try {
        ctx.createMediaElementSource(m).connect(musicGainRef.current);
        wired = true;
      } catch (_) {}
    }
    m.volume = wired ? 1 : 0;
    m.play()
      .then(() => rampMusic(MUSIC_IDLE, 1200))
      .catch(() => { if (musicRef.current === m) musicRef.current = null; });
  }, [ensureCtx, rampMusic]);

  const stopMusic = useCallback((fadeMs = 0) => {
    if (musicRampRef.current) clearInterval(musicRampRef.current);
    const m = musicRef.current;
    musicRef.current = null;
    if (!m) return;
    const g = musicGainRef.current;
    if (fadeMs > 0 && !m.paused && g && ctxRef.current) {
      const t = ctxRef.current.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(g.gain.value, t);
      g.gain.linearRampToValueAtTime(0, t + fadeMs / 1000);
      setTimeout(() => m.pause(), fadeMs + 30);
    } else if (fadeMs > 0 && !m.paused && !m.muted) {
      const steps = 6;
      const v0 = m.volume;
      let i = 0;
      const iv = setInterval(() => {
        i++;
        m.volume = Math.max(0, v0 * (1 - i / steps));
        if (i >= steps) { clearInterval(iv); m.pause(); }
      }, Math.max(fadeMs / steps, 20));
    } else {
      m.pause();
    }
  }, []);

  useEffect(() => () => {
    stopMusic();
    if (ctxRef.current) { ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
  }, [stopMusic]);

  const setLock = (v) => { lockedRef.current = v; setLocked(v); };

  const unlock = () => {
    unlockedRef.current = true;
    setUnlocked(true);
    ss.set("onb_audio_unlocked", "1");
    ensureCtx();
    startMusic();
  };

  const stopAudio = useCallback((fadeMs = 0) => {
    tokenRef.current++;
    fallbackTimers.current.forEach(clearTimeout);
    fallbackTimers.current = [];
    const a = audioRef.current;
    audioRef.current = null;
    const nodes = narrNodesRef.current;
    narrNodesRef.current = null;
    setSubIdx(-1);
    rampMusic(MUSIC_IDLE, 500);
    if (!a) return;
    const cleanup = () => {
      a.pause();
      if (nodes) { try { nodes.src.disconnect(); nodes.g.disconnect(); } catch (_) {} }
    };
    if (fadeMs > 0 && !a.paused && nodes && ctxRef.current) {
      const t = ctxRef.current.currentTime;
      nodes.g.gain.cancelScheduledValues(t);
      nodes.g.gain.setValueAtTime(nodes.g.gain.value, t);
      nodes.g.gain.linearRampToValueAtTime(0, t + fadeMs / 1000);
      setTimeout(cleanup, fadeMs + 30);
    } else if (fadeMs > 0 && !a.paused && !a.muted) {
      const steps = 6;
      const v0 = a.volume;
      let i = 0;
      const iv = setInterval(() => {
        i++;
        a.volume = Math.max(0, v0 * (1 - i / steps));
        if (i >= steps) { clearInterval(iv); cleanup(); }
      }, Math.max(fadeMs / steps, 20));
    } else {
      cleanup();
    }
  }, [rampMusic]);

  const playScene = useCallback((s) => {
    stopAudio();
    const token = ++tokenRef.current;
    const weights = s.subs.en.map((x) => x.length);
    const total = weights.reduce((a, b) => a + b, 0);
    const bounds = [];
    let acc = 0;
    for (const w of weights) { acc += w; bounds.push(acc / total); }
    const a = new Audio(sceneAudio(s));
    a.preload = "auto";
    a.muted = mutedRef.current;
    const ctx = ensureCtx();
    let wired = false;
    if (ctx && masterGainRef.current) {
      try {
        const src = ctx.createMediaElementSource(a);
        const g = ctx.createGain();
        g.gain.value = 1;
        src.connect(g);
        g.connect(masterGainRef.current);
        narrNodesRef.current = { src, g };
        wired = true;
      } catch (_) {}
    }
    a.volume = wired ? 1 : volumeRef.current;
    audioRef.current = a;
    a.ontimeupdate = () => {
      if (token !== tokenRef.current) return;
      const dur = a.duration || s.dur;
      const frac = Math.min(a.currentTime / dur, 0.999);
      let i = bounds.findIndex((b) => frac < b);
      if (i === -1) i = weights.length - 1;
      setSubIdx(i);
    };
    a.onended = () => {
      if (token === tokenRef.current) setSubIdx(-1);
      rampMusic(MUSIC_IDLE, 500);
    };
    a.play()
      .then(() => {
        if (token === tokenRef.current) setSubIdx(0);
        if (!mutedRef.current) rampMusic(MUSIC_DUCK, 400);
      })
      .catch(() => {
        // Playback blocked (e.g. no user gesture yet) — drive subtitles on a timer
        if (token !== tokenRef.current) return;
        setSubIdx(0);
        bounds.slice(0, -1).forEach((b, i) => {
          fallbackTimers.current.push(
            setTimeout(() => { if (token === tokenRef.current) setSubIdx(i + 1); }, b * s.dur * 1000)
          );
        });
        fallbackTimers.current.push(
          setTimeout(() => { if (token === tokenRef.current) setSubIdx(-1); }, s.dur * 1000)
        );
      });
  }, [stopAudio, rampMusic, ensureCtx]);

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    ss.set("onb_muted", next ? "1" : "0");
    if (audioRef.current) audioRef.current.muted = next;
    if (musicRef.current) musicRef.current.muted = next;
  };

  const replayNarration = () => {
    unlock();
    playScene(sc);
  };

  const onVolume = (e) => {
    const v = Math.min(Math.max(Number(e.target.value) / 100, 0), 1);
    volumeRef.current = v;
    setVolume(v);
    ss.set("onb_volume", String(v));
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = v;
    } else {
      if (audioRef.current) audioRef.current.volume = v;
      if (musicRef.current) musicRef.current.volume = MUSIC_IDLE * v;
    }
  };

  const goTo = useCallback((target) => {
    if (lockedRef.current || target < 1 || target > TOTAL || target === nRef.current) return;
    ensureCtx();
    setLock(true);
    stopAudio(300);
    setStage("out");
    setTimeout(() => navigate(`/my-stoicos/onboarding/${target}${replay ? "?replay=1" : ""}`), FADE + 60);
  }, [FADE, navigate, stopAudio, replay, ensureCtx]);
  const goToRef = useRef(goTo);
  goToRef.current = goTo;

  const finish = useCallback(async () => {
    if (lockedRef.current) return;
    setLock(true);
    stopAudio(300);
    stopMusic(600);
    setStage("out");
    // Fire the completion write immediately (inside the tap gesture) and
    // retry — a dropped request here would bounce the student back into
    // the onboarding from the dashboard.
    const completed = (async () => {
      if (replay) return true;
      for (let i = 0; i < 3; i++) {
        try {
          await axios.post(`${API}/api/me/onboarding/complete`, {}, { withCredentials: true });
          return true;
        } catch (_) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }
      return false;
    })();
    await new Promise((r) => setTimeout(r, FADE + 60));
    await completed;
    if (!replay) {
      try { await refresh(); } catch (_) {}
    }
    navigate("/my-stoicos", { replace: true });
  }, [FADE, navigate, refresh, stopAudio, stopMusic, replay]);

  const onPrimary = () => {
    ensureCtx();
    if (n === 1 && !unlockedRef.current) {
      unlock();
      playScene(sc);
      return;
    }
    if (isFinal) finish(); else goTo(n + 1);
  };

  // Scene lifecycle: persist progress, preload neighbours, fade in, start narration
  useEffect(() => {
    const cur = SCENES[n - 1];
    if (!replay) {
      axios.post(`${API}/api/me/onboarding/scene`, { scene: n }, { withCredentials: true }).catch(() => {});
    }
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    [n, n + 1, n - 1].forEach((i) => {
      if (i < 1 || i > TOTAL) return;
      const s = SCENES[i - 1];
      const im = new Image();
      im.src = isMobile ? sceneImgMobile(s) : sceneImg(s);
      if (i === n + 1) {
        const au = document.createElement("audio");
        au.preload = "auto";
        au.src = sceneAudio(s);
      }
    });
    const timers = [
      setTimeout(() => setStage("in"), PAUSE),
      setTimeout(() => setLock(false), PAUSE + FADE),
      setTimeout(() => {
        if (unlockedRef.current) {
          startMusic();
          playScene(cur);
        } else if (n > 1) {
          // Mid-onboarding refresh: wait for the first deliberate tap
          const h = () => { unlock(); if (nRef.current === n) playScene(cur); };
          pointerHandler.current = h;
          window.addEventListener("pointerdown", h, { once: true });
        }
      }, PAUSE + Math.round(FADE * 0.6)),
    ];
    return () => {
      timers.forEach(clearTimeout);
      if (pointerHandler.current) {
        window.removeEventListener("pointerdown", pointerHandler.current);
        pointerHandler.current = null;
      }
      stopAudio();
    };
  }, [n, FADE, playScene, stopAudio, startMusic, replay]);

  // Keyboard navigation (desktop)
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" && nRef.current < TOTAL) goToRef.current(nRef.current + 1);
      else if (e.key === "ArrowLeft") goToRef.current(nRef.current - 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (loading) {
    return <main className="fixed inset-0 z-[60] bg-[#050505]" style={{ width: "100vw", height: "100dvh" }} />;
  }
  if (!user || (user.onboarding_completed && !replay)) {
    return <Navigate to="/my-stoicos" replace />;
  }

  const subs = sc.subs[lang] || sc.subs.en;
  const activeSub = subIdx >= 0 && stage === "in" ? subs[Math.min(subIdx, subs.length - 1)] : null;
  const showBegin = n === 1 && !unlocked;

  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const s0 = touchStart.current;
    touchStart.current = null;
    if (!s0) return;
    const dx = e.changedTouches[0].clientX - s0.x;
    const dy = e.changedTouches[0].clientY - s0.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > 2 * Math.abs(dy)) {
      if (dx < 0 && !isFinal && !showBegin) goTo(n + 1);
      else if (dx > 0) goTo(n - 1);
    }
  };

  const ctrlBtn =
    "w-10 h-10 rounded-full border border-bone/30 bg-black/35 backdrop-blur-sm flex items-center justify-center text-bone/80 hover:border-gold hover:text-gold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold";

  return (
    <main
      data-testid="onboarding-scenes"
      className="fixed inset-0 z-[60] bg-[#050505] overflow-hidden"
      style={{ width: "100vw", height: "100dvh" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <style>{`
        @keyframes onbFade { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .onb-fade { animation: onbFade 1.2s ease both; }
        @keyframes onbSub { from { opacity: 0; } to { opacity: 1; } }
        .onb-sub { animation: onbSub 0.35s ease both; }
        .onb-vol { accent-color: #C5A059; height: 4px; cursor: pointer; }
        @keyframes kbIn { from { transform: scale(1); } to { transform: scale(1.07); } }
        @keyframes kbSoft { from { transform: scale(1); } to { transform: scale(1.035); } }
        @keyframes kbOut { from { transform: scale(1.08); } to { transform: scale(1.01); } }
        @keyframes kbDrift { from { transform: scale(1.07) translateX(0.8%); } to { transform: scale(1.07) translateX(-0.8%); } }
        .onb-kb-in { animation: kbIn 26s linear both; }
        .onb-kb-soft { animation: kbSoft 26s linear both; }
        .onb-kb-out { animation: kbOut 26s linear both; }
        .onb-kb-drift { animation: kbDrift 26s linear both; }
        .onb-img { object-position: var(--onb-focus-d, 50% 50%); }
        @media (max-width: 767px) { .onb-img { object-position: var(--onb-focus-m, 50% 50%); } }
        @media (prefers-reduced-motion: reduce) {
          .onb-fade, .onb-sub { animation-duration: 0.01s; }
          .onb-kb-in, .onb-kb-soft, .onb-kb-out, .onb-kb-drift { animation: none; }
        }
      `}</style>

      <div key={`img-${sc.id}`} className="absolute inset-0 overflow-hidden">
        <picture>
          <source media="(max-width: 767px)" srcSet={sceneImgMobile(sc)} />
          <img
            data-testid={`onboarding-scene-image-${n}`}
            src={sceneImg(sc)}
            alt={t(`onb.ch${n}.title`)}
            draggable="false"
            className={`absolute inset-0 w-full h-full object-cover select-none onb-img onb-kb-${sc.kb}`}
            style={{
              "--onb-focus-d": sc.focusD,
              "--onb-focus-m": sc.focusM,
              transformOrigin: sc.origin,
            }}
          />
        </picture>
      </div>

      {/* Scrims for text legibility — never letterbox, always cover */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.34) 34%, rgba(0,0,0,0) 64%)" }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-56 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)" }}
      />

      {/* Overlay content */}
      <div
        className="absolute inset-0 flex flex-col justify-between px-6 sm:px-12 lg:px-16"
        style={{
          paddingTop: "calc(2rem + env(safe-area-inset-top))",
          paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))",
        }}
      >
        <div className="self-end flex flex-col items-end gap-3">
          <p
            data-testid="onboarding-progress"
            className="text-[10px] tracking-[0.3em] uppercase text-bone/45 select-none"
          >
            {String(n).padStart(2, "0")} / {String(TOTAL).padStart(2, "0")}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="onboarding-mute-button"
              aria-label={muted ? t("onb.sound.unmute") : t("onb.sound.mute")}
              onClick={toggleMute}
              className={ctrlBtn}
            >
              {muted ? <VolumeX size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
            </button>
            <button
              type="button"
              data-testid="onboarding-replay-button"
              aria-label={t("onb.replay")}
              onClick={replayNarration}
              className={ctrlBtn}
            >
              <RotateCcw size={15} strokeWidth={1.5} />
            </button>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(volume * 100)}
            onChange={onVolume}
            data-testid="onboarding-volume-slider"
            aria-label={t("onb.volume")}
            className="onb-vol w-[5.5rem]"
          />
          {!isFinal && (
            <button
              type="button"
              data-testid="onboarding-skip-button"
              onClick={finish}
              disabled={locked}
              className="mt-1.5 text-[10px] tracking-[0.22em] uppercase text-bone/40 hover:text-gold transition-colors disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              {t("onb.skipIntro")}
            </button>
          )}
        </div>

        <div key={`text-${sc.id}`} className="max-w-md onb-fade -mt-6 sm:-mt-10">
          <div
            data-testid="onboarding-chapter-number"
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-gold/50 flex items-center justify-center mb-6"
          >
            <span style={serif} className="text-xl sm:text-2xl text-gold">{String(n).padStart(2, "0")}</span>
          </div>
          <h1
            data-testid="onboarding-scene-title"
            className="font-inscription text-2xl sm:text-4xl lg:text-5xl text-bone tracking-[0.12em] uppercase mb-4"
          >
            {t(`onb.ch${n}.title`)}
          </h1>
          {isFinal && (
            <p
              data-testid="onboarding-final-subtext"
              style={serif}
              className="text-lg sm:text-xl text-bone/90 leading-relaxed max-w-[19rem] sm:max-w-sm [text-shadow:0_1px_14px_rgba(0,0,0,0.85)]"
            >
              {t("onb.ch6.desc")}
            </p>
          )}
          <div className="flex items-center gap-2.5 mt-4" aria-hidden>
            <span className="h-px w-12 bg-gold/70" />
            <span className="w-1.5 h-1.5 rotate-45 bg-gold/80" />
            <span className="h-px w-12 bg-gold/70" />
          </div>
        </div>

        <div>
          {/* Synchronized subtitle — one segment at a time */}
          <div className="flex justify-center mb-4 sm:mb-6 min-h-[4.5rem] sm:min-h-[4rem] items-end" aria-live="polite">
            {activeSub && (
              <p
                key={`${sc.id}-${subIdx}`}
                data-testid="onboarding-subtitle"
                style={serif}
                className="onb-sub text-center text-lg sm:text-xl text-bone leading-relaxed max-w-xl px-4 py-2 rounded-md bg-black/45 backdrop-blur-[2px]"
              >
                {activeSub}
              </p>
            )}
          </div>

          {/* Bottom controls */}
          <div className="flex items-center justify-between gap-4">
            <button
              data-testid="onboarding-back-button"
              onClick={() => goTo(n - 1)}
              disabled={n === 1 || locked}
              className="inline-flex items-center gap-2 border border-bone/30 text-[11px] tracking-[0.24em] uppercase text-bone/85 hover:border-gold hover:text-gold disabled:opacity-25 disabled:hover:border-bone/30 disabled:hover:text-bone/85 px-5 sm:px-8 py-3.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("onb.back")}
            </button>

            <div className="hidden sm:flex items-center gap-2" aria-hidden>
              {SCENES.map((s, i) => (
                <span key={s.id} className={`w-1.5 h-1.5 rounded-full ${i + 1 === n ? "bg-gold" : "bg-bone/25"}`} />
              ))}
            </div>

            <button
              data-testid={showBegin ? "onboarding-begin-button" : isFinal ? "onboarding-enter-button" : "onboarding-continue-button"}
              onClick={onPrimary}
              disabled={locked}
              className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] tracking-[0.24em] uppercase text-ink bg-gold hover:bg-gold-hover disabled:opacity-60 px-6 sm:px-9 py-3.5 sm:py-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-bone"
            >
              {showBegin ? t("onb.begin") : isFinal ? t("onb.enter") : (
                <>
                  {t("onb.continue")} <ArrowRight size={14} strokeWidth={1.5} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Cinematic fade-through-black transition layer */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[#050505] pointer-events-none transition-opacity ease-in-out"
        style={{ opacity: stage === "in" ? 0 : 1, transitionDuration: `${FADE}ms` }}
      />
    </main>
  );
}
